import { PayLandlordButton } from "@/components/nds/windows/finance/flow/PayLandlord";
import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ArrowUpRight, Banknote, CalendarCheck2, CreditCard, DollarSign, Home, Inbox, Landmark, Siren, ShieldCheck, TrendingUp, Users, Wallet, ClipboardCheck } from "lucide-react";
import { apiClient, useAuth } from "@/lib/auth";
import { useNexOs } from "@/lib/nexOs";
import { isSuperAdminUser } from "@/lib/placement";
import { useFloatingWindows } from "@/lib/floatingWindows";
import ArrangeBoard from "@/components/nds/ArrangeBoard";
import { DeskPeriodBar } from "@/components/nds/PeriodToggle";

const usd = (n) => (Math.abs(n) >= 1000 ? `$${Math.round(n).toLocaleString()}` : `$${Number(n).toFixed(2)}`);
const show = (t, fmt) => (t.value == null ? "—" : fmt === "currency" ? usd(t.value) : Number(t.value).toLocaleString());

/** Live pull of /desk/pulse/<kind>, refreshed every 30s like the other LIVE tiles (and right away when the ledger changes). */
function usePulse(kind, params = null, live = false) {
  const [s, setS] = useState({ loading: true, tiles: {}, error: null, busy: false });
  const key = JSON.stringify(params);
  const skip = params?.only === "";
  const seq = React.useRef(0);
  const load = useCallback(() => {
    if (skip) { setS({ loading: false, tiles: {}, error: null, busy: false }); return Promise.resolve(); }
    const n = ++seq.current;
    setS((x) => ({ ...x, busy: true }));
    return apiClient.get(`/desk/pulse/${kind}`, { params: params || {} })
      .then(({ data }) => { if (n === seq.current) setS({ loading: false, tiles: data.tiles || {}, range: data.range, error: null, busy: false }); })
      .catch((e) => { if (n === seq.current) setS((x) => ({ ...x, loading: false, busy: false, error: e?.response?.data?.detail || "Couldn't load these numbers" })); });
  }, [kind, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!live) return undefined;
    let t;
    const h = () => { clearTimeout(t); t = setTimeout(load, 400); };
    window.addEventListener("fin:ledger-changed", h);
    window.addEventListener("fin:paycheck-logged", h);
    return () => { clearTimeout(t); window.removeEventListener("fin:ledger-changed", h); window.removeEventListener("fin:paycheck-logged", h); };
  }, [load, live]);
  return s;
}

function PulseTile({ id, label, Icon, t, fmt, onOpen, loading, dim, extra }) {
  const err = t?.error;
  return (
    <button type="button" onClick={onOpen} data-testid={`pulse-tile-${id}`} title={err || t?.hint || undefined}
      className="nds-card h-full w-full p-5 text-left flex flex-col hover:border-nex-violet hover:shadow-[0_18px_40px_rgba(124,92,255,0.20)] transition-[border-color,box-shadow]">
      <div className="flex items-start justify-between gap-2">
        <div className="nds-label">{label}</div>
        <div className="w-9 h-9 shrink-0 grid place-items-center bg-nex-navy text-nex-goldbright rounded-sm"><Icon className="w-4 h-4" strokeWidth={1.8} /></div>
      </div>
      <div className={`mt-4 text-3xl md:text-4xl nds-num tabular-nums transition-opacity ${dim ? "opacity-40 animate-pulse" : ""}`} data-testid={`pulse-value-${id}`}>
        {loading ? <span className="text-nex-muted animate-pulse">…</span> : err || !t ? <span className="text-nex-muted" data-testid={`pulse-error-${id}`}>—</span> : show(t, fmt)}
        {!loading && t?.charged != null && !err && <span className="text-base text-nex-muted"> of {usd(t.charged)}</span>}
      </div>
      <div className="mt-2 text-[0.7rem] text-nex-chrome leading-snug" data-testid={`pulse-sub-${id}`}>{err ? <span className="inline-flex items-center gap-1 text-amber-300"><AlertTriangle className="w-3 h-3" />{err}</span> : t?.sub}</div>
      {t?.hint && <div className="mt-1 text-[0.6rem] text-nex-muted leading-snug" data-testid={`pulse-hint-${id}`}>{t.hint}</div>}
      {!err && t?.lines?.length > 0 && <div className="mt-3 pt-2 border-t border-white/10 space-y-1" data-testid={`pulse-lines-${id}`}>
        {t.lines.map((l) => (
          <div key={l.key} className="flex items-baseline gap-2 text-[0.7rem]" data-testid={`pulse-line-${id}-${l.key}`}>
            <span className="flex-1 text-nex-chrome leading-snug">{l.label}</span>
            <span className={`font-mono tabular-nums ${l.tone === "bad" ? "text-red-300" : l.tone === "warn" ? "text-amber-300" : l.tone === "ok" ? "text-emerald-300" : l.tone === "dim" ? "text-nex-muted" : "text-nex-silver"}`}>{usd(l.value)}</span>
          </div>))}
      </div>}
      <div className="mt-auto pt-3 flex items-center gap-2">
        <span className="nds-pill nds-pill--live" style={{ padding: "3px 8px", fontSize: "0.6rem" }}>LIVE</span>
        {extra}
        <span className="ml-auto inline-flex items-center gap-1 font-mono text-[0.6rem] tracking-[0.18em] uppercase text-nex-chrome">Open <ArrowUpRight className="w-3 h-3" /></span>
      </div>
    </button>
  );
}

const openResidentDashboard = () => {
  const go = () => document.querySelector('[data-testid="resident-dashboard"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  const btn = [...document.querySelectorAll("button[aria-expanded]")].find((b) => b.textContent.includes("Resident Dashboard"));
  if (btn && btn.getAttribute("aria-expanded") !== "true") { btn.click(); setTimeout(go, 500); } else go();
};

const PROGRAM = [
  ["checkins", "CHECK-INS TO REVIEW", CalendarCheck2, "int", (o) => o("re_checkins", {})],
  ["pillars_week", "PILLARS VERIFIED THIS WEEK", ShieldCheck, "int", (o) => o("pillar_board", {})],
  ["phase_advances", "PHASE ADVANCES (30D)", TrendingUp, "int", () => openResidentDashboard()],
  ["on_track", "RESIDENTS ON TRACK", Users, "int", () => openResidentDashboard()],
];

/** Tier 1 top block (replaces the old-platform placeholder tiles). */
export function ProgramPulse() {
  const s = usePulse("program");
  const { openWindow } = useFloatingWindows();
  const by = Object.fromEntries(PROGRAM.map((x) => [x[0], x]));
  const tile = (k) => { const [id, label, Icon, fmt, open] = by[k]; return <PulseTile id={id} label={label} Icon={Icon} fmt={fmt} t={s.error ? { error: s.error } : s.tiles[id]} loading={s.loading} onOpen={() => open(openWindow)} />; };
  return <div data-testid="nds-program-pulse"><ArrangeBoard board="desk_program_pulse" ids={PROGRAM.map((x) => x[0])} labels={Object.fromEntries(PROGRAM.map((x) => [x[0], x[1]]))}
    gridClass="grid-cols-1 sm:grid-cols-2 xl:grid-cols-4" renderTile={tile} label="tiles" /></div>;
}

const rep = (rg, extra, title) => ({ ...(rg?.report || {}), ...extra, title: `Finance Report · ${title}${rg ? ` · ${rg.label}` : ""}` });
// [id, label, icon, format, open(openWindow, tile, range), source: cash | ops | money, finance-only]
const MONEY = [
  ["total_cash", "TOTAL CASH COLLECTED", Banknote, "currency", (o, t, rg) => o("fin_reports", rep(rg, { cat: "paychecks", catLabel: "Paychecks" }, "Money in")), "cash", true],
  ["rent_paid", "RENT COLLECTED", Home, "currency", (o, t, rg) => o("fin_reports", rep(rg, { cat: "accounts", catLabel: "Cash & rent escrow" }, "Rent")), "cash", true],
  ["cm_paid", "PACT FEE COLLECTED", ClipboardCheck, "currency", (o, t, rg) => o("fin_reports", rep(rg, { cat: "fees", catLabel: "Program fees" }, "PACT Fee")), "cash", true],
  ["loc_paid", "LOC PAID", Landmark, "currency", (o, t, rg) => o("fin_reports", rep(rg, { cat: "loans", catLabel: "Line of Credit" }, "Line of Credit")), "cash", true],
  ["cash_on_hand", "CASH IN HAND · NOW", DollarSign, "currency", (o) => o("fin_reports", {}), "ops", true],
  ["dues_week", "DUES COLLECTED VS CHARGED · THIS WEEK", Wallet, "currency", (o, t) => o("fin_reports", { week: t?.week, view: "dues", title: "Finance Report · Rent & case management" }), "money", true],
  ["loc_owed", "LINE OF CREDIT OWED", CreditCard, "currency", (o) => o("fin_reports", {}), "money", true],
  ["incidents", "OPEN INCIDENTS", Siren, "int", (o) => o("re_incidents", {}), "money", false],
  ["problems", "OPEN PROBLEMS INBOX", Inbox, "int", (o) => o("ops_problems", {}), "money", false],
];
const OLD = ["dues_week", "loc_owed", "incidents", "problems"];
const CASH = MONEY.filter((x) => x[5] === "cash").map((x) => x[0]);

/** Tier 1 second block: real money that came in (Week / Month / All time) + Cash in Hand now. Old tiles wait in the Hidden tray. */
export function MoneyOpsBar() {
  const { user } = useAuth();
  const sa = isSuperAdminUser(user);
  const { opsKpis, opsOk, desk } = useNexOs();
  const [shown, setShown] = useState(null);
  const want = (src) => (shown ? shown.filter((id) => MONEY.find((x) => x[0] === id)?.[5] === src).join(",") : "");
  const cash = usePulse("cash", sa && shown ? { period: desk.view, ...(desk.at ? { at: desk.at } : {}), only: want("cash") } : { only: "" }, true);
  const s = usePulse("money", shown ? { only: want("money") } : { only: "" });
  const { openWindow, openFinReport } = useFloatingWindows();
  const fw = (kind, payload) => (kind === "fin_reports" && payload?.title ? openFinReport(payload) : openWindow(kind, payload));
  const cards = user?.visible_cards;
  const fin = sa || !Array.isArray(cards) || cards.some((c) => String(c).startsWith("cap_fin"));
  const rows = MONEY.filter((x) => (fin || !x[6]) && (sa || x[5] !== "cash"));
  const by = Object.fromEntries(rows.map((x) => [x[0], x]));
  const ops = opsKpis?.cash_on_hand;
  const tile = (k) => {
    const [id, label, Icon, fmt, open, src] = by[k];
    const p = src === "cash" ? cash : s;
    const t = src === "ops" ? (ops ? { value: ops.value, sub: "Bank account balances as of right now · a live balance, so it doesn't change with Week / Month / All time" } : opsOk === null ? null : { error: "Couldn't load Cash in Hand" }) : p.error ? { error: p.error } : p.tiles[id];
    return <PulseTile id={id} label={label} Icon={Icon} fmt={fmt} t={t} loading={src === "ops" ? !ops && opsOk === null : p.loading || (!t && !p.error)} dim={src === "cash" && cash.busy}
      onOpen={() => open(fw, t, cash.range)} extra={id === "rent_paid" && sa ? <PayLandlordButton as="span" testid="desk-pay-landlord-btn" /> : null} />;
  };
  return (
    <div className="flex flex-col gap-4" data-testid="nds-money-ops">
      <div className="flex flex-wrap items-center justify-between gap-2"><div className="nds-label-dim">// MONEY AND OPERATIONS</div>
        <div className="flex flex-wrap items-center gap-3">
          {sa && <DeskPeriodBar rg={cash.range} busy={cash.busy} testid="money-period" />}
          <span className="font-mono text-[0.7rem] text-nex-chrome tracking-[0.18em] uppercase">Read-only · live ledger</span></div></div>
      <ArrangeBoard board={sa ? "desk_money_cash" : "desk_money_ops"} ids={rows.map((x) => x[0])} labels={Object.fromEntries(rows.map((x) => [x[0], x[1]]))} defaultHidden={sa ? OLD.filter((x) => by[x]) : []}
        hiddenLabel="Hidden tiles" onShown={setShown} gridClass="grid-cols-1 sm:grid-cols-2 xl:grid-cols-5" renderTile={tile} label="tiles" />
      {shown && shown.some((id) => CASH.includes(id)) && <p className="text-[0.7rem] text-nex-chrome" data-testid="money-ops-caption">Rent, CM and LOC Paid show where money went. They may not add up to Total Cash Collected.</p>}
    </div>
  );
}
